import React from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import {
  useGetTopic,
  useGetPageBookmarks,
  useGetToolboxByTopicId,
  useGetLesson,
} from "../../api/queries";
import Spinner from "../../componentLibrary/Spinner";
import TabGroup from "../../componentLibrary/TabGroup";
import { NewlyAddedBadgeWrapper } from "../../componentLibrary/NewlyAddedBadge";
import { analytics } from "../../services/AnalyticsService";
import { ErrorDisplay } from "../../utils/ErrorHandling";
import ToolboxTopNavigation from "../../components/ToolboxTopNavigation";
import ToolboxBottomNavigation from "../../components/ToolboxBottomNavigation";
import { FadingNavigation } from "../../componentLibrary/FadingBar";
import SkillsTab from "./components/SkillsTab";
import JournalTab from "./components/JournalTab";

const MyToolboxPage = () => {
  const { topicId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const defaultTabIndex = tabParam === "journal" ? 1 : 0;

  const fromUrl = location.state?.from || `/topics/${topicId}`;
  const lessonIdMatch = fromUrl.match(/\/section\/([^/?]+)/);
  const lessonIdFromUrl = lessonIdMatch ? lessonIdMatch[1] : undefined;

  const {
    data: topic,
    isError,
    isLoading,
    error: topicError,
  } = useGetTopic(topicId);

  const { data: lesson, isLoading: isLessonLoading } =
    useGetLesson(lessonIdFromUrl);

  const {
    data: bookmarks,
    isLoading: isBookmarksLoading,
    isError: isBookmarksError,
    error: bookmarksError,
  } = useGetPageBookmarks(undefined, topicId);
  const { data: toolbox, isLoading: isToolboxLoading } = useGetToolboxByTopicId(
    topicId || "",
  );

  // Track toolbox open
  React.useEffect(() => {
    if (toolbox?._id && topicId) {
      analytics.track("toolbox_opened", {
        category: "Toolbox",
        elementId: toolbox._id,
        additionalDetails: {
          topicId: topicId,
          fromUrl: fromUrl,
        },
      });
    }
  }, [toolbox?._id, topicId, fromUrl]);

  if (
    isLoading ||
    isBookmarksLoading ||
    isToolboxLoading ||
    (lessonIdFromUrl && isLessonLoading)
  )
    return <Spinner className="mt-4 mx-auto" />;
  if (isError || isBookmarksError || !topic)
    return (
      <ErrorDisplay error={topicError || bookmarksError} messageSource="api" />
    );

  const hasUnreadBookmarks = bookmarks?.some((bookmark) => {
    if (!bookmark.createdAt) return false;
    if (!toolbox?.skillsReadAt) return false;
    return new Date(bookmark.createdAt) > new Date(toolbox.skillsReadAt);
  });

  const handleTabChange = (index: number) => {
    const tabName = index === 0 ? "my_pages" : "my_journal";
    analytics.track("toolbox_tab_selected", {
      category: "Toolbox",
      elementId: toolbox?._id,
      additionalDetails: {
        topicId: topicId,
        tabName: tabName,
      },
    });
  };

  const tabData = [
    {
      label: (
        <NewlyAddedBadgeWrapper show={hasUnreadBookmarks} size="small">
          <span className="px-5">My Pages</span>
        </NewlyAddedBadgeWrapper>
      ),
      content: <SkillsTab topicId={topicId} toolboxId={toolbox?._id} />,
    },
    {
      label: "My Journal",
      content: <JournalTab topicId={topicId} />,
    },
  ];

  const showBottomNav = true; // Always show bottom navigation

  return (
    <>
      <div className="sticky top-0 left-0 right-0 z-10">
        <ToolboxTopNavigation
          topic={topic}
          fromUrl={fromUrl}
          lessonTitle={lesson?.title}
        />
      </div>
      <div className={`max-w-96 mx-auto ${showBottomNav ? "pb-24" : ""}`}>
        <div className="flex justify-center items-center my-4">
          <TabGroup
            tabs={tabData}
            defaultSelectedIndex={defaultTabIndex}
            onTabChange={handleTabChange}
          />
        </div>
      </div>
      <FadingNavigation bottomOffset={0} className="bg-white shadow-md">
        <ToolboxBottomNavigation fromUrl={fromUrl} topicId={topicId || ""} />
      </FadingNavigation>
    </>
  );
};

export default MyToolboxPage;
